import { Link, useNavigate, useLocation } from "react-router-dom"
import { useState, useEffect } from "react"
import { useAuth, AuthLoadingScreen } from "./AuthProvider"
import { createLogger } from '../utils/logger'
import OAuthButtons, { EmailDivider } from './auth/OAuthButtons'
import { posthog, isPostHogReady } from '../services/posthog'

const logger = createLogger('auth')

const inputClass =
  'w-full rounded-md border border-neutral-300 px-3 py-2.5 text-sm text-neutral-900 placeholder:text-neutral-400 shadow-sm focus:border-neutral-900 focus:outline-none focus:ring-1 focus:ring-neutral-900 disabled:bg-neutral-50'

function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, user } = useAuth()
  const forgotPath = "/forgot-password"
  const signupPath = "/signup"
  const signupSuccess = location.state?.signupSuccess
  const oauthError = location.state?.oauthError
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isPostHogReady()) {
      posthog.capture('$pageview', { $current_url: window.location.href, page: 'login' })
    }
  }, [])

  useEffect(() => {
    if (oauthError) setError(oauthError)
  }, [oauthError])

  useEffect(() => {
    try {
      const flash = sessionStorage.getItem('ls_auth_flash')
      if (flash) {
        sessionStorage.removeItem('ls_auth_flash')
        setError(flash)
      }
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    if (!user) return
    const from = location.state?.from?.pathname || "/app"
    navigate(from, { replace: true })
  }, [user, location.state, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setLoading(true)

    const { error: loginError } = await login(email, password)

    if (loginError) {
      logger.warn('Login failed', { view: 'login', auth_method: 'email' })
      setError(loginError.message)
      setLoading(false)
    }
  }

  if (user) {
    return <AuthLoadingScreen />
  }

  return (
    <div className="flex min-h-screen flex-col bg-white text-neutral-900">
      <div className="flex flex-1 flex-col items-center justify-center px-6 py-12">
        <Link to="/" className="mb-10 block">
          <img
            src="/ls-logo.svg"
            alt="LandSentry"
            className="mx-auto h-14 w-auto md:h-16"
            width={280}
            height={70}
          />
        </Link>

        <div className="w-full max-w-sm rounded-lg border border-neutral-200 bg-white p-8 shadow-sm">
          <h1 className="text-lg font-semibold tracking-tight text-neutral-900">Sign in</h1>
          <p className="mt-1 text-sm text-neutral-500">Use your LandSentry account.</p>

          {signupSuccess && (
            <div
              className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800"
              role="status"
            >
              {signupSuccess}
            </div>
          )}

          {error && (
            <div
              className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              role="alert"
            >
              {error}
            </div>
          )}

          <div className="mt-6">
            <OAuthButtons mode="signin" disabled={loading} onError={setError} />
          </div>

          <EmailDivider mode="signin" />

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="login-email" className="sr-only">
                Email
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                placeholder="Email"
                className={inputClass}
              />
            </div>
            <div>
              <label htmlFor="login-password" className="sr-only">
                Password
              </label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                placeholder="Password"
                className={inputClass}
              />
            </div>
            <div className="flex justify-end">
              <Link
                to={forgotPath}
                className="text-sm font-medium text-neutral-600 underline decoration-neutral-300 underline-offset-2 hover:text-neutral-900 hover:decoration-neutral-900"
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center rounded-md bg-neutral-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {loading ? (
                <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
              ) : (
                "Log in"
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-neutral-600">
            Don&apos;t have an account?{" "}
            <Link
              to={signupPath}
              className="font-medium text-neutral-900 underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-900"
            >
              Sign up
            </Link>
          </p>
        </div>

        <Link
          to="/"
          className="mt-10 text-sm text-neutral-500 transition-colors hover:text-neutral-900"
        >
          ← Back to home
        </Link>
      </div>
    </div>
  )
}

export default LoginPage
